import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { BusinessAppearanceCard } from '@/components/settings/BusinessAppearanceCard';
import { MyAppearanceCard } from '@/components/settings/MyAppearanceCard';
import { KitchenTimesCard } from '@/components/settings/KitchenTimesCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PrintWidthEditor, defaultPrintWidth } from '@/components/settings/PrintWidthEditor';
import { 
  ArrowLeft, 
  Building2, 
  Palette, 
  DollarSign, 
  Bell,
  Users,
  Save,
  Loader2,
  Printer,
  Settings2,
  Coins
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    currency: 'USD',
    timezone: 'America/New_York',
    tax_rate: 0,
    service_charge: 0,
    require_table_confirmation: false,
    suggested_tip_percentages: [10, 15, 20],
    theme: 'light'
  });

  // POS Module settings
  const [posSettings, setPosSettings] = useState({
    tips_enabled: true,
    tips_mode: 'individual',
    print_enabled: true,
    auto_print_on_confirm: false,
    print_kitchen_enabled: true,
    print_bar_enabled: false,
    show_print_preview: false,
    waiter_can_charge: false
  });
  const [printers, setPrinters] = useState([]);
  const [newPrinter, setNewPrinter] = useState({
    name: '',
    printer_type: 'browser',
    ip_address: '',
    port: 9100,
    areas: ['kitchen'],
    paper_width: 80
  });

  useEffect(() => {
    fetchBusiness();
    fetchPosSettings();
    fetchPrinters();
  }, []);

  const fetchPosSettings = async () => {
    try {
      const response = await axios.get(`${API}/business/settings`);
      setPosSettings(response.data);
    } catch (error) {
      console.error('Error fetching POS settings:', error);
    }
  };

  const fetchPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers`);
      setPrinters(response.data || []);
    } catch (error) {
      console.error('Error fetching printers:', error);
    }
  };

  const handleSavePosSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business/settings`, posSettings);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPrinter = async () => {
    if (!newPrinter.name) {
      toast.error('Nombre requerido');
      return;
    }
    try {
      await axios.post(`${API}/printers`, newPrinter);
      toast.success('Impresora agregada');
      fetchPrinters();
      setNewPrinter({ name: '', printer_type: 'browser', ip_address: '', port: 9100, areas: ['kitchen'], paper_width: 80 });
    } catch (error) {
      toast.error(error.response?.data?.detail?.[0]?.msg || 'Error al agregar impresora');
    }
  };

  const handleDeletePrinter = async (printerId) => {
    try {
      await axios.delete(`${API}/printers/${printerId}`);
      toast.success('Impresora eliminada');
      fetchPrinters();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/business`, { withCredentials: true });
      setBusiness(response.data);
      setForm({
        name: response.data.name || '',
        description: response.data.description || '',
        address: response.data.address || '',
        phone: response.data.phone || '',
        currency: response.data.currency || 'USD',
        timezone: response.data.timezone || 'America/New_York',
        tax_rate: response.data.tax_rate || 0,
        service_charge: response.data.service_charge || 0,
        require_table_confirmation: !!response.data.require_table_confirmation,
        suggested_tip_percentages: response.data.suggested_tip_percentages || [10, 15, 20],
        theme: response.data.theme || 'light'
      });
    } catch (error) {
      console.error('Error fetching business:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/business`, form, { withCredentials: true });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Configuración</h1>
            <p className="text-sm text-muted-foreground">
              Ajustes de tu negocio
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
        {user?.role === 'business_admin' && (
          <Button variant="outline" onClick={() => navigate('/diagnostico-ip')} data-testid="ip-diagnostics-link">
            Diagnóstico de IP
          </Button>
        )}
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </>
          )}
        </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <Tabs defaultValue="business" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="business">
              <Building2 className="w-4 h-4 mr-2" />
              Negocio
            </TabsTrigger>
            <TabsTrigger value="payments">
              <DollarSign className="w-4 h-4 mr-2" />
              Pagos
            </TabsTrigger>
            <TabsTrigger value="pos-modules">
              <Settings2 className="w-4 h-4 mr-2" />
              Módulos POS
            </TabsTrigger>
            <TabsTrigger value="appearance">
              <Palette className="w-4 h-4 mr-2" />
              Apariencia
            </TabsTrigger>
          </TabsList>

          {/* Business Tab */}
          <TabsContent value="business">
            <Card>
              <CardHeader>
                <CardTitle>Información del Negocio</CardTitle>
                <CardDescription>
                  Datos básicos que verán tus clientes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nombre del Negocio</Label>
                  <Input
                    placeholder="Mi Restaurante"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="business-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    placeholder="Breve descripción de tu negocio"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input
                      placeholder="Calle, Ciudad"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teléfono</Label>
                    <Input
                      placeholder="+54 11 1234-5678"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Moneda</Label>
                    <Select
                      value={form.currency}
                      onValueChange={(value) => setForm({ ...form, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD - Dólar</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="ARS">ARS - Peso Argentino</SelectItem>
                        <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                        <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Zona Horaria</Label>
                    <Select
                      value={form.timezone}
                      onValueChange={(value) => setForm({ ...form, timezone: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/New_York">Nueva York (EST)</SelectItem>
                        <SelectItem value="America/Buenos_Aires">Buenos Aires (ART)</SelectItem>
                        <SelectItem value="America/Mexico_City">Ciudad de México (CST)</SelectItem>
                        <SelectItem value="America/Bogota">Bogotá (COT)</SelectItem>
                        <SelectItem value="Europe/Madrid">Madrid (CET)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Configuración de Pagos</CardTitle>
                <CardDescription>
                  Impuestos, cargos por servicio y propinas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tasa de Impuesto (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="0"
                      value={form.tax_rate}
                      onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Se aplicará automáticamente a cada pedido
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Cargo por Servicio (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="0"
                      value={form.service_charge}
                      onChange={(e) => setForm({ ...form, service_charge: parseFloat(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Cargo adicional por servicio
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-xl border border-border p-3">
                  <div>
                    <Label>El personal debe confirmar la mesa antes de aceptar pedidos</Label>
                    <p className="text-xs text-muted-foreground">Los pedidos desde el QR esperan a que un mozo confirme la mesa</p>
                  </div>
                  <Switch checked={form.require_table_confirmation} data-testid="require-table-confirmation-switch"
                    onCheckedChange={(v) => setForm({ ...form, require_table_confirmation: v })} />
                </div>

                <div className="space-y-2">
                  <Label>Propinas Sugeridas (%)</Label>
                  <div className="flex gap-2">
                    {form.suggested_tip_percentages.map((tip, index) => (
                      <Input
                        key={index}
                        type="number"
                        className="w-20"
                        value={tip}
                        onChange={(e) => {
                          const newTips = [...form.suggested_tip_percentages];
                          newTips[index] = parseInt(e.target.value) || 0;
                          setForm({ ...form, suggested_tip_percentages: newTips });
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Se mostrarán como opciones al cerrar la cuenta
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* POS Modules Tab */}
          <TabsContent value="pos-modules">
            <div className="space-y-6">
              <KitchenTimesCard settings={posSettings} onChange={setPosSettings} onSave={handleSavePosSettings} saving={saving} />
              {/* Tips Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Coins className="w-5 h-5 text-yellow-500" />
                    Propinas
                  </CardTitle>
                  <CardDescription>
                    Configura cómo se manejan las propinas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Habilitar propinas</Label>
                      <p className="text-xs text-muted-foreground">Permite ingresar propinas al cerrar cuentas</p>
                    </div>
                    <Switch
                      checked={posSettings.tips_enabled}
                      onCheckedChange={(checked) => setPosSettings({...posSettings, tips_enabled: checked})}
                    />
                  </div>
                  
                  {posSettings.tips_enabled && (
                    <div className="space-y-2">
                      <Label>Modo de distribución</Label>
                      <Select 
                        value={posSettings.tips_mode} 
                        onValueChange={(value) => setPosSettings({...posSettings, tips_mode: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="individual">Individual - Cada mozo recibe su propina</SelectItem>
                          <SelectItem value="pool">Pool - Se divide entre todos los mozos del turno</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Print Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">Cobros del mozo</CardTitle>
                  <CardDescription>Quién puede cobrar mesas, partes y comensales</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="waiter-can-charge">El mozo puede cobrar</Label>
                      <p className="text-xs text-muted-foreground">Si está apagado, solo caja y administración cobran</p>
                    </div>
                    <Switch
                      id="waiter-can-charge"
                      data-testid="settings-waiter-can-charge"
                      checked={!!posSettings.waiter_can_charge}
                      onCheckedChange={(checked) => setPosSettings({...posSettings, waiter_can_charge: checked})}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Printer className="w-5 h-5 text-blue-500" />
                    Impresión
                  </CardTitle>
                  <CardDescription>
                    Configura comandas y tickets
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Habilitar impresión</Label>
                      <p className="text-xs text-muted-foreground">Activa la impresión de comandas y tickets</p>
                    </div>
                    <Switch
                      checked={posSettings.print_enabled}
                      onCheckedChange={(checked) => setPosSettings({...posSettings, print_enabled: checked})}
                    />
                  </div>

                  {posSettings.print_enabled && (
                    <>
                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Imprimir automáticamente al confirmar pedido</Label>
                          <p className="text-xs text-muted-foreground">La comanda se envía a cocina/bar al confirmar</p>
                        </div>
                        <Switch
                          checked={posSettings.auto_print_on_confirm}
                          onCheckedChange={(checked) => setPosSettings({...posSettings, auto_print_on_confirm: checked})}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Impresión en Cocina</Label>
                          <p className="text-xs text-muted-foreground">Envía comandas a la cocina</p>
                        </div>
                        <Switch
                          checked={posSettings.print_kitchen_enabled}
                          onCheckedChange={(checked) => setPosSettings({...posSettings, print_kitchen_enabled: checked})}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Impresión en Bar</Label>
                          <p className="text-xs text-muted-foreground">Envía comandas al bar</p>
                        </div>
                        <Switch
                          checked={posSettings.print_bar_enabled}
                          onCheckedChange={(checked) => setPosSettings({...posSettings, print_bar_enabled: checked})}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>Mostrar vista previa antes de imprimir</Label>
                          <p className="text-xs text-muted-foreground">Muestra el ticket en pantalla antes de enviar a la impresora</p>
                        </div>
                        <Switch
                          checked={posSettings.show_print_preview}
                          onCheckedChange={(checked) => setPosSettings({...posSettings, show_print_preview: checked})}
                        />
                      </div>
                    </>
                  )}

                  <Button onClick={handleSavePosSettings} disabled={saving} className="mt-4">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar Configuración
                  </Button>
                </CardContent>
              </Card>

              {/* Printers List */}
              <Card>
                <CardHeader>
                  <CardTitle>Impresoras Configuradas</CardTitle>
                  <CardDescription>
                    Agrega impresoras térmicas o de red
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Existing printers */}
                  {printers.length > 0 ? (
                    <div className="space-y-2">
                      {printers.map((printer) => (
                        <div key={printer.printer_id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                          <div>
                            <p className="font-medium">{printer.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {printer.printer_type === 'browser' ? 'Navegador/PDF' : 
                               printer.printer_type === 'thermal_network' ? `Red: ${printer.ip_address}:${printer.port}` :
                               'USB'}
                              {' • '}{printer.areas.join(', ')} • Papel {printer.paper_width}mm · imprime {printer.printable_mm}mm
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                          <PrintWidthEditor printer={printer} onSaved={fetchPrinters} />
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeletePrinter(printer.printer_id)}
                          >
                            Eliminar
                          </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">No hay impresoras configuradas</p>
                  )}

                  {/* Add new printer */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Agregar nueva impresora</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Nombre</Label>
                        <Input 
                          value={newPrinter.name}
                          onChange={(e) => setNewPrinter({...newPrinter, name: e.target.value})}
                          placeholder="Ej: Cocina Principal"
                        />
                      </div>
                      <div>
                        <Label>Tipo</Label>
                        <Select 
                          value={newPrinter.printer_type}
                          onValueChange={(value) => setNewPrinter({...newPrinter, printer_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="browser">Navegador/PDF</SelectItem>
                            <SelectItem value="thermal_network">Térmica de Red</SelectItem>
                            <SelectItem value="thermal_usb">Térmica USB</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newPrinter.printer_type === 'thermal_network' && (
                        <>
                          <div>
                            <Label>IP</Label>
                            <Input 
                              value={newPrinter.ip_address}
                              onChange={(e) => setNewPrinter({...newPrinter, ip_address: e.target.value})}
                              placeholder="192.168.1.100"
                            />
                          </div>
                          <div>
                            <Label>Puerto</Label>
                            <Input 
                              type="number"
                              value={newPrinter.port}
                              onChange={(e) => setNewPrinter({...newPrinter, port: parseInt(e.target.value) || 9100})}
                            />
                          </div>
                        </>
                      )}
                      
                      <div>
                        <Label>Área</Label>
                        <Select 
                          value={newPrinter.areas[0]}
                          onValueChange={(value) => setNewPrinter({...newPrinter, areas: [value]})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kitchen">Cocina</SelectItem>
                            <SelectItem value="bar">Bar</SelectItem>
                            <SelectItem value="cashier">Caja</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Ancho papel</Label>
                        <Select 
                          value={String(newPrinter.paper_width)}
                          onValueChange={(value) => setNewPrinter({...newPrinter, paper_width: parseInt(value), print_width_mm: defaultPrintWidth(value)})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="58">58mm</SelectItem>
                            <SelectItem value="80">80mm</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Ancho imprimible (mm)</Label>
                        <Input type="number" min={30} max={newPrinter.paper_width}
                          value={newPrinter.print_width_mm ?? defaultPrintWidth(newPrinter.paper_width)}
                          onChange={(e) => setNewPrinter({...newPrinter, print_width_mm: e.target.value === '' ? null : parseInt(e.target.value)})}
                          data-testid="new-printer-width-input" />
                      </div>
                    </div>
                    <Button onClick={handleAddPrinter} className="mt-3">
                      <Printer className="w-4 h-4 mr-2" />
                      Agregar Impresora
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="space-y-6">
            <BusinessAppearanceCard />
            <MyAppearanceCard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
